import React, { Dispatch, useContext, useEffect, useMemo, useState } from 'react';
import { Heading, LiliDropdown, LiliDropdownItem, LiliDropdownSize } from '@lili/lili-frontend-common';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PeriodTypeFilter } from './periodTypeFilter';
import { PeriodDatepickerFilter } from './periodDatepickerFilter';
import { IProfitAndLossFilters, ProfitAndLossPeriodType, ReportOutputType } from '../constants/types';
import {
  datesArr,
  getFilterObject,
  handleSetCustomDate,
  handleSetPeriodDates,
  handleDownload,
  printIframe,
} from '../helpers';
import { ProfitAndLossContext } from '../pages';
import { useStore } from '../../../store';
import { cleanOffsetFromDate, getEndOfDay, getStartOfDay } from '../../../helpers/datesAndTime.helper';
import { ReactComponent as DownloadIcon } from '../../../assets/icons/svg/shared/ic-shared-download.svg';
import { VIEWS_MAP } from '../../../router/constants';
import { useGenerateExpenseReport } from '../../../hooks/mutations/useGenerateExpenseReport';

interface IProps {
  filterObject: IProfitAndLossFilters;
  setFilterObject: Dispatch<React.SetStateAction<IProfitAndLossFilters>>;
}

enum ReportExportType {
  Download,
  Print,
  Excel,
}

export enum ReportType {
  Detailed = 'Detailed',
  Summarized = 'Summarized',
}

export const FiltersWrapper = ({ filterObject, setFilterObject }: IProps) => {
  const { t } = useTranslation('reportsEN');
  const location = useLocation();

  const items = [
    { title: t('REPORTS.FILE_TYPE.DETAILED'), value: ReportType.Detailed },
    { title: t('REPORTS.FILE_TYPE.SUMMARIZED'), value: ReportType.Summarized },
  ];

  const exportItems = [
    { title: t('REPORTS.DOWNLOAD_OPTION'), value: ReportExportType.Download },
    { title: t('REPORTS.EXCEL_OPTION'), value: ReportExportType.Excel },
    { title: t('REPORTS.PRINT_OPTION'), value: ReportExportType.Print },
  ].filter((item) => !(item.value === ReportExportType.Excel && location.pathname !== `/${VIEWS_MAP.EXPENSES}`));

  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);
  const [showPeriodOption, setShowPeriodOption] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [fileType, setFileType] = useState<LiliDropdownItem | undefined>(items[0]);
  const FlowContext = useContext(ProfitAndLossContext);
  const isExpenses = location.pathname === `/${VIEWS_MAP.EXPENSES}`;
  const {
    customer: { tcTime },
  } = useStore((state) => state.summary);
  const {
    user: { customerId },
  } = useStore((state) => state);

  const { mutateAsync: generateExcel } = useGenerateExpenseReport({
    reportData: { ...filterObject, outputType: ReportOutputType.EXCEL },
    customerId,
    reportType: fileType?.value,
  });

  const generateEndDate = useMemo(() => {
    if (FlowContext?.filterObject.period?.periodType !== ProfitAndLossPeriodType.CUSTOM) {
      return FlowContext?.filterObject.period?.startDate!;
    }
    return startDate;
  }, [FlowContext?.filterObject.period?.periodType]);

  const generateStartDate = useMemo(() => {
    if (FlowContext?.filterObject.period?.periodType !== ProfitAndLossPeriodType.CUSTOM) {
      return FlowContext?.filterObject.period?.endDate!;
    }
    return endDate;
  }, [FlowContext?.filterObject.period?.periodType]);

  useEffect(() => {
    FlowContext?.setReportType(fileType?.value);
  }, [fileType]);
  const reportData = (() => {
    switch (location.pathname.replace('/', '')) {
      case VIEWS_MAP.STATEMENT_OF_CASH_FLOWS:
        return {
          iFrameUrl: FlowContext?.IframeUrlCashFlow,
          fileName: FlowContext?.cashFlowFileName,
        };
      case VIEWS_MAP.PROFIT_AND_LOSS:
        return {
          iFrameUrl: FlowContext?.iframeUrlProfitAndLoss,
          fileName: FlowContext?.profitAndLossFileName,
        };
      case VIEWS_MAP.EXPENSES:
        return {
          iFrameUrl: FlowContext?.iframeUrlExpenses,
          fileName: FlowContext?.expensesFileName,
        };
      default:
        return {
          iFrameUrl: '',
          fileName: '',
        };
    }
  })();
  const onExportOptionSelect = (option: LiliDropdownItem) => {
    switch (option.value) {
      case ReportExportType.Print:
        printIframe('report');
        break;
      case ReportExportType.Download:
        handleDownload(reportData.iFrameUrl, reportData.fileName);
        break;
      case ReportExportType.Excel:
        generateExcel().then(({ IframeUrl, parsedName }) => handleDownload(IframeUrl, parsedName));
        break;
      default:
        break;
    }
  };

  const handleDateChange = (type: 'endDate' | 'startDate', d: Date) => {
    setFilterObject(({ period, ...rest }) =>
      (period
        ? {
          period: { ...period, [type]: d },
          ...rest,
        }
        : {
          period: { endDate: new Date(), startDate: new Date(), [type]: d },
          ...rest,
        }));
  };
  const handleCustomPeriod = () => {
    setShowPeriodOption(false);
    setShowDatePicker(true);
    FlowContext?.setFilterObject({
      ...filterObject,
      period: {
        endDate: !endDate ? new Date() : endDate,
        startDate: !startDate ? new Date() : startDate,
        periodType: ProfitAndLossPeriodType.CUSTOM,
      },
    });
    if (FlowContext?.dateRange.endDate !== null && FlowContext?.dateRange.startDate !== null) {
      setShowPeriodOption((prevState) => !prevState);
      handleSetCustomDate({
        periodType: ProfitAndLossPeriodType.CUSTOM,
        endDate: generateEndDate,
        startDate: generateStartDate,
      });
    }
  };

  const handleNonCustomPeriodType = (type: ProfitAndLossPeriodType) => {
    setStartDate(null);
    setEndDate(null);
    setShowPeriodOption((prevState) => !prevState);
    FlowContext?.setFilterObject(
      getFilterObject({
        dates: handleSetPeriodDates(type, tcTime),
        periodType: type,
      }),
    );
  };
  const handleSelectPeriodType = (type: ProfitAndLossPeriodType) => {
    // select custom period type flow
    if (type === ProfitAndLossPeriodType.CUSTOM) {
      handleCustomPeriod();
    } else {
      handleNonCustomPeriodType(type);
    }
  };

  const activePeriod = useMemo(() => {
    const filteredPeriod = datesArr.filter((p) => p.type === filterObject.period?.periodType)[0];
    if (filteredPeriod) {
      return filteredPeriod;
    }
    return datesArr[0];
  }, [filterObject.period]);

  const onChange = (dates: Date[]) => {
    const [start, end] = dates;
    handleDateChange('endDate', start);
    handleDateChange('startDate', end == null ? new Date() : end);
    if (start && end) {
      FlowContext?.setIsDatesRangeComplete(true);
      const modEndDate = getEndOfDay(end);
      setShowDatePicker(false);
      FlowContext?.setFilterObject({
        ...filterObject,
        period: {
          startDate: cleanOffsetFromDate(start),
          endDate: cleanOffsetFromDate(modEndDate),
          periodType: ProfitAndLossPeriodType.CUSTOM,
        },
      });
    }
    setStartDate(start);
    setEndDate(end);
    handleSetPeriodDates(ProfitAndLossPeriodType.CUSTOM, tcTime, start, end);
    FlowContext?.setDateRange({
      ...(start ? { startDate: start } : {}),
      ...(end ? { endDate: end } : {}),
    });
  };

  useEffect(() => {
    if (!endDate && startDate) {
      const startDateObject = getStartOfDay(new Date(FlowContext?.dateRange.startDate || ''));
      const endDateObject = getEndOfDay(new Date());
      // Check if startDate is greater than endDate
      if (startDateObject > endDateObject) {
        endDateObject.setTime(startDateObject.getTime());
      }

      FlowContext?.setFilterObject({
        ...filterObject,
        period: {
          startDate: getStartOfDay(startDateObject).toISOString(),
          endDate: getEndOfDay(endDateObject).toISOString(),
          periodType: ProfitAndLossPeriodType.CUSTOM,
        },
      });

      FlowContext?.setIsDatesRangeComplete(true);
    }
  }, [showDatePicker]);
  const handleToggleDropdown = (filterType: string) => {
    if (filterType === 'periodType') {
      if (showPeriodOption) {
        return;
      }
      FlowContext?.setIsDatesRangeComplete(false);
      setShowPeriodOption((prevState) => !prevState);
    }
    if (filterType === 'minMaxDateType') {
      if (showDatePicker) {
        return;
      }
      FlowContext?.setIsDatesRangeComplete(false);
      setShowDatePicker((prevState) => !prevState);
    }
  };

  return (
    <div className='flex justify-between w-full'>
      <div className='pr-3 flex w-full'>
        <PeriodTypeFilter
          selectedPeriod={activePeriod}
          showPeriodOption={showPeriodOption}
          handleToggleDropdown={handleToggleDropdown}
          filterObject={filterObject}
          handleSelectPeriodType={handleSelectPeriodType}
          setShowPeriodOption={setShowPeriodOption}
        />
        <PeriodDatepickerFilter
          showDatePicker={showDatePicker}
          handleToggleDropdown={handleToggleDropdown}
          startDate={ProfitAndLossPeriodType.CUSTOM ? startDate : generateEndDate}
          endDate={ProfitAndLossPeriodType.CUSTOM ? endDate : generateStartDate}
          onChange={onChange}
          setShowDatePicker={setShowDatePicker}
        />
      </div>
      <div className='flex gap-2 items-center'>
        {isExpenses && (
          <>
            <Heading.H9 id='file-type-title' className='w-[74px] mb-[8px]'>
              {t('REPORTS.FILE_TYPE.TITLE')}
            </Heading.H9>
            <div className='w-[159px]'>
              <LiliDropdown
                items={items}
                title=''
                onSelect={setFileType}
                value={fileType}
                size={LiliDropdownSize.Small}
              />
            </div>
          </>
        )}
        <div className='w-[189px]'>
          <LiliDropdown
            items={exportItems}
            title=''
            onSelect={onExportOptionSelect}
            value={{ title: t('REPORTS.DOWNLOAD_AND_PRINT'), value: t('REPORTS.DOWNLOAD_AND_PRINT') }}
            size={LiliDropdownSize.Small}
            Icon={DownloadIcon}
          />
        </div>
      </div>
    </div>
  );
};
